import {Box, createStyles, Group, Kbd, Modal, Table, Text} from '@mantine/core';
import React from 'react';

import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as keyboard from 'util/Keyboard';
import {assertNever} from 'util/Types';

import {AnalysisFlowchartAction} from '../actions/AnalysisFlowchartActionTypes';
import {useAnalysisFlowchartContext} from '../AnalysisFlowchartContext';

export interface ShortcutsModalProps {
  opened: boolean;
  onClose: () => void;
}

interface ShortcutRowProps {
  title: string;
  tokens: Array<string | {key: string} | {icon: BenchlingIconName}>;
  description?: string;
}

const useShortcutRowStyles = createStyles(() => ({
  // Prevent extra spacing at the top of the modal body
  modalBody: {
    paddingTop: '0 !important',
  },
  // Make description rows look a part of a larger row, without affecting the layout of the title and tokens
  descriptionRow: {
    '&&& > td': {
      borderTop: 'none',
      paddingTop: 0,
    },
  },
}));

function ShortcutRow({title, tokens, description}: ShortcutRowProps): JSX.Element {
  const {classes} = useShortcutRowStyles();
  return (
    <>
      <tr>
        <td>
          <Text fz="lg" weight={600}>
            {title}
          </Text>
        </td>
        <td>
          <Group spacing="xs" noWrap={true}>
            {tokens.map((token, index) =>
              typeof token === 'string' ? (
                <Text key={index} fz="lg" ml={index ? 'xs' : undefined} mr="xs" mb="md" mt="md">
                  {token}
                </Text>
              ) : 'key' in token ? (
                <Kbd key={index} size="m" fz="lg" fw="normal" py="sm" px="md">
                  {token.key}
                </Kbd>
              ) : 'icon' in token ? (
                <Box key={index} component="span" fz="xl">
                  <BenchlingIcon icon={token.icon} />
                </Box>
              ) : (
                assertNever(token)
              )
            )}
          </Group>
        </td>
      </tr>
      {description && (
        <tr className={classes.descriptionRow}>
          <td />
          <td>
            <Text fz="md">{description}</Text>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Modal that lists the shortcuts available in the analysis flowchart.
 */
export default function ShortcutsModal({opened, onClose}: ShortcutsModalProps): JSX.Element {
  const {getActionInfo} = useAnalysisFlowchartContext();
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useShortcutRowStyles();
  const canCopy = getActionInfo(AnalysisFlowchartAction.COPY_TO_CLIPBOARD).enabled;
  const canPaste = getActionInfo(AnalysisFlowchartAction.PASTE_FROM_CLIPBOARD).enabled;
  const modifierKeyText = keyboard.getOsSpecificShortcutText('Ctrl');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Shortcuts"
      size="md"
      classNames={{body: classes.modalBody}}
    >
      <Table verticalSpacing="sm" pb="sm">
        <tbody>
          <ShortcutRow title="Highlight steps" tokens={['Hold', {key: 'Shift'}, '+', 'Click and drag']} />
          <ShortcutRow title="Select multiple steps" tokens={[{key: modifierKeyText}, '+', 'Click']} />
          <ShortcutRow title="Select all steps" tokens={[{key: modifierKeyText}, '+', {key: 'A'}]} />
          <ShortcutRow title="Delete selected steps" tokens={[{key: 'Backspace'}]} />
          <ShortcutRow
            title="Delete a connection"
            tokens={['Click on a connection then press', {key: 'Backspace'}]}
          />
          <ShortcutRow
            title="Connect steps"
            tokens={['Click', {icon: 'circle-arrow-right'}, 'on a step and drag to another step']}
          />
          <ShortcutRow
            title="Add step in a new branch"
            tokens={['Click', {icon: 'circle-arrow-right'}, 'on a step and drag out']}
          />
          {canCopy && (
            <ShortcutRow
              title="Copy to clipboard"
              tokens={[{key: modifierKeyText}, '+', {key: 'C'}]}
              description="Copy selected steps' configurations"
            />
          )}
          {canPaste && (
            <ShortcutRow
              title="Paste from clipboard"
              tokens={[{key: modifierKeyText}, '+', {key: 'V'}]}
              description="Paste steps, file or dataset API IDs, or file or dataset URLs"
            />
          )}
        </tbody>
      </Table>
    </Modal>
  );
}
